// LA SCHEDA GUIDA dello strato, con la grammatica rev.1 del tutorial del solitario (dispatch `qsolitario/dispatch-31-messaggi.md` punti 1-5, KB `services/qlash-telegram-app/ui/2026-10-26-solitario-tutorial-messaggi-rev1.md`, 80cb0609)
// in DUE formati (PR-2 della zona, I-1229): la PIENA e` la rev.1 (altezza uguale in tutti i passi, riquadro anche in lettura); la COMPATTA prende le misure di #861. La scheda di partenza:
// copiato da src/components/solitario/TutorialSolitario.tsx:473-534 @ cad51507af021388ceb3a5de34eb61259665077d (il coach e la riga dei bottoni), SENZA cio` che faceva il «lampo»: «Avanti» che compariva e spariva fuori da una riga fissa, la nota che allungava il coach.
//
// RIALLINEAMENTO A #861 (la correzione del Solitario, cantiere qsolitario-orcr-31): la grammatica e le misure della compatta sono quelle di TMA#861, FUSA in main allo sha e30f58abd42bfcf46c036dfc31f620f529ba2b48
// (27/10 01:01Z; il contenuto e` quello della testa c38f382e, identico). Cosa prende la compatta (`[@media(max-height:720px)]`, o forzata dal gioco): padding stretto, testo 14/20, riquadro di stato 60 px, riga d'azione 40 px (l'area
// di tocco resta 44: v. AREA_DI_TOCCO), altezza che segue il passo (niente cella unica dei passi), niente riquadro nei passi di lettura, il gap dello strato a 4 px. Differenze RIMASTE rispetto a #861, una per riga:
//  1. #861 non tiene mai la cella unica dei passi; la PIENA si` (rev.1 punto 3, seconda parte), perche` e` la scelta di disegno di tma-ui-orcb ([D] 27/10 00:15Z).
//  2. #861 ha un solo riquadro di stato a 84 px in piena (`h-[5.25rem]`); qui in piena il riquadro e` alto quanto la voce piu` lunga, che sta nella stessa cella.
//  3. #861 mette nel riquadro le pastiglie del gesto e le note dei punti (`GESTI`, `NOTE_DI_TESTO`); qui il riquadro e` icona + testo: i gesti e i punti sono del gioco, non della zona.
//  4. #861 ha l'icona dello stato in un cerchio da 32 px con fondo; qui l'icona e` nuda da 18 px.
//  5. #861 schiarisce il verde del «giusto» col bianco (`color-mix` 55%, sotto i 4,5:1 il `success` pieno); qui il «giusto» ha ancora `text-success` pieno: da misurare e da portare qui con #861 (APERTO).
//  6. #861 ha il contenitore `rounded-2xl border p-4`, la zona `rounded-xl` senza bordo (la scheda sta sotto un tavolo vero, non su un fondo vuoto).
//  7. #861 dissolve in 150 ms (`DISSOLVENZA_MS`), la zona in 160 (`ritmo.ts`: il tetto della spec).
//
// LA LETTURA CON `dopo` IN COMPATTA ([D] (c) di tma-ui-orcb, 27/10 03:55:58Z): nei passi di lettura il riquadro di stato non ha altezza (e` `sr-only`, non `hidden`: resta nell'albero dell'accessibilita` e `role="status"`
// annuncia `turnoAltrui`), quindi il motivo di «Avanti» spento va nel bottone stesso: stesso nodo, Hourglass e `turnoAltrui` al posto di «Avanti →», riga da 40 px. In piena non cambia niente. Mentre la coda e` in corso
// il riquadro prende il tono e il `data-riscontro` di 'altrui', non quelli del riscontro prima.
import { useLayoutEffect, useRef } from 'react';
import type { MouseEvent } from 'react';
import { ArrowRight, Check, Hand, Hourglass, Info, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { useMovimentoRidotto } from '@/hooks/useMovimentoRidotto';
import { cn } from '@/lib/cn';
import type { Riscontro } from '@/lib/tutorial/esecutore';
import { DISSOLVENZA_MS } from '@/lib/tutorial/ritmo';

/**
 * Il FORMATO della scheda. `piena` e` la rev.1: tutti i testi nella stessa cella, quindi la stessa altezza in ogni passo, e il riquadro di stato anche nei passi di lettura. `compatta` prende le misure di #861
 * (padding stretto, testo 14/20, riquadro 60 px, riga 40 px): l'altezza segue il passo e nei passi di lettura il riquadro non c'e`. `auto` (il default dello strato) e` la piena, e diventa compatta da sola sotto i 720 px
 * di altezza (`@media(max-height:720px)`, come #861): 360×640 e` compatta, 390×844 e` piena.
 */
export type FormatoScheda = 'auto' | 'piena' | 'compatta';

/** Un testo che il riquadro di stato puo` mostrare. In `piena` tutti stanno nella stessa cella: il riquadro e` alto quanto il piu` lungo e non cambia altezza. `altrui`: tocca agli altri (la coda di `dopo` in corso). */
export interface VoceStato {
  chiave: string;
  riscontro: Riscontro | 'altrui';
  testo: string;
}

export type AzioneScheda = 'attesa' | 'mostramelo' | 'avanti';

export interface PropsSchedaGuida {
  idTitolo: string;
  /** I testi di TUTTI i passi: in `piena` stanno nella stessa cella, cosi` la scheda ha la stessa altezza in ogni passo (rev.1 punto 3, seconda parte); in `compatta` si disegna solo quello in corso. */
  passi: readonly { id: string; titolo: string; testo: string }[];
  indice: number;
  voci: readonly VoceStato[];
  /** La chiave della voce in vista. */
  attiva: string;
  /** 'altrui' mentre la coda di `dopo` si mostra (e il gioco ha dato `turnoAltrui`): il tono e il `data-riscontro` del riquadro non sono quelli del riscontro prima. */
  riscontro: Riscontro | 'altrui';
  azione: AzioneScheda;
  /** «Avanti» si puo` toccare: no finche` ci sono mosse di `dopo` da mostrare (il bottone c'e` gia`, disabilitato, e non cambia nodo). */
  avantiAttivo: boolean;
  mostra: boolean;
  /** Un passo di sola lettura e` in corso: in `compatta` il riquadro di stato non ha altezza (`sr-only`) e «Avanti», mentre la coda e` in corso, porta `turnoAltrui`. */
  lettura: boolean;
  formato: FormatoScheda;
  onAvanti: () => void;
  onMostramelo: () => void;
  testi: { avanti: string; mostramelo: string; faiLaMossa: string; turnoAltrui?: string };
}

const ICONA: Record<Riscontro | 'altrui', typeof Hand> = { attesa: Hand, nonLi: TriangleAlert, altra: Info, giusto: Check, altrui: Hourglass };
/**
 * I TONI del riquadro di stato: fondo + testo. Il TESTO di ogni tono ha contrasto ≥ 4,5:1 sul suo fondo in ogni tema (verify-tutorial (11)): `fg-muted`, `warning` e `success` PURI non ci arrivano sulla teal (3,99, 4,26,
 * 3,21), e sono token condivisi con centinaia di file fuori dalla zona, quindi si cambia il testo QUI, non il token: un `color-mix` verso `fg`, che tiene la tinta del tono e ne alza la luminanza. Mai il fondo.
 * Le classi sono scritte per intero (Tailwind le legge dal sorgente) e la verify le capisce cosi`: `bg-<token>[/<n>]`, `text-<token>`, `text-[color-mix(in_srgb,var(--color-A)_P%,var(--color-B))]`.
 * La v3 RESTA COM'ERA (tma-ui-orcb, 20:53:54Z, KB f0579b02): i suoi toni passavano gia` (≥ 6,01). Il testo di prima lo rimettono tre regole in `src/v3/v3.css`, agganciate a `data-riquadro-stato` e `data-riscontro` che il riquadro gia` espone:
 * qui NON c'e` nessun marcatore della v3 (l'attributo di `applicaV3` vive solo nei sorgenti della v3, `tools/verify-v3.ts`). `altra` e `altrui` non cambiano.
 */
const TONO: Record<Riscontro | 'altrui', string> = {
  attesa: 'bg-bg-tertiary text-[color-mix(in_srgb,var(--color-fg-muted)_70%,var(--color-fg))]',
  nonLi: 'bg-warning/12 text-[color-mix(in_srgb,var(--color-warning)_75%,var(--color-fg))]',
  altra: 'bg-bg-tertiary text-fg',
  giusto: 'bg-success/15 text-[color-mix(in_srgb,var(--color-success)_55%,var(--color-fg))]',
  altrui: 'bg-bg-tertiary text-fg',
};

/**
 * L'AREA DI TOCCO ≥ 44 anche con la riga a 40 px (compatta): lo stesso pseudo-elemento centrato, trasparente e fuori dal layout della primitiva Button (`AREA_DI_TOCCO`, Button.tsx:81, per le taglie `sm`).
 * Con la riga a 48 (piena) `max(100%, 2.75rem)` e` il 100%: non cambia niente.
 */
const AREA_DI_TOCCO =
  "relative after:absolute after:left-1/2 after:top-1/2 after:h-[max(100%,2.75rem)] after:w-[max(100%,2.75rem)] after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']";
/**
 * Il riquadro di stato nei passi di LETTURA: in piena com'e`; in compatta (e in `auto` sotto i 720 px) `sr-only`, senza altezza ma nell'albero dell'accessibilita` (con `hidden` il `role="status"` non annuncerebbe
 * `turnoAltrui`). Senza le classi di misura della compatta (`h-…`, `px-…`): `sr-only` ha le sue. Scritte per intero, e fuori da `CLASSI`, che e` per elementi in tre formati.
 */
const RIQUADRO_IN_LETTURA: Record<FormatoScheda, string> = {
  piena: 'grid rounded-lg px-3 py-2',
  compatta: 'sr-only',
  auto: 'grid rounded-lg px-3 py-2 [@media(max-height:720px)]:sr-only',
};

/**
 * Le classi dei tre formati, scritte PER INTERO (Tailwind le legge dal sorgente: una classe composta a pezzi non esisterebbe nel CSS). `auto` = la piena + le stesse classi della compatta dietro
 * `[@media(max-height:720px)]:`; l'ordine delle varianti fa vincere la compatta sotto i 720 px.
 */
interface Classi {
  piena: string;
  compatta: string;
  auto: string;
}
const CLASSI = {
  scheda: { piena: 'flex shrink-0 flex-col rounded-xl bg-bg-secondary gap-2 px-3 py-2', compatta: 'flex shrink-0 flex-col rounded-xl bg-bg-secondary gap-1.5 px-2.5 py-1.5', auto: 'flex shrink-0 flex-col rounded-xl bg-bg-secondary gap-2 px-3 py-2 [@media(max-height:720px)]:gap-1.5 [@media(max-height:720px)]:px-2.5 [@media(max-height:720px)]:py-1.5' },
  contenuto: { piena: 'flex flex-col gap-2', compatta: 'flex flex-col gap-1.5', auto: 'flex flex-col gap-2 [@media(max-height:720px)]:gap-1.5' },
  badge: { piena: 'flex shrink-0 items-center justify-center rounded-full bg-brand font-extrabold text-brand-fg size-7 text-sm', compatta: 'flex shrink-0 items-center justify-center rounded-full bg-brand font-extrabold text-brand-fg size-6 text-xs', auto: 'flex shrink-0 items-center justify-center rounded-full bg-brand font-extrabold text-brand-fg size-7 text-sm [@media(max-height:720px)]:size-6 [@media(max-height:720px)]:text-xs' },
  titolo: { piena: 'font-extrabold text-fg text-lg leading-6', compatta: 'font-extrabold text-fg text-base leading-5', auto: 'font-extrabold text-fg text-lg leading-6 [@media(max-height:720px)]:text-base [@media(max-height:720px)]:leading-5' },
  testo: { piena: 'text-fg text-base leading-6', compatta: 'text-fg text-sm leading-5', auto: 'text-fg text-base leading-6 [@media(max-height:720px)]:text-sm [@media(max-height:720px)]:leading-5' },
  riquadro: { piena: 'grid rounded-lg px-3 py-2', compatta: 'grid rounded-lg h-[3.75rem] content-center overflow-hidden px-2.5 py-0.5', auto: 'grid rounded-lg px-3 py-2 [@media(max-height:720px)]:h-[3.75rem] [@media(max-height:720px)]:content-center [@media(max-height:720px)]:overflow-hidden [@media(max-height:720px)]:px-2.5 [@media(max-height:720px)]:py-0.5' },
  voce: { piena: 'flex items-start gap-2 font-semibold [grid-area:1/1] text-sm leading-5', compatta: 'flex items-start gap-2 font-semibold [grid-area:1/1] text-[13px] leading-4', auto: 'flex items-start gap-2 font-semibold [grid-area:1/1] text-sm leading-5 [@media(max-height:720px)]:text-[13px] [@media(max-height:720px)]:leading-4' },
  nascosto: { piena: 'invisible', compatta: 'hidden', auto: 'invisible [@media(max-height:720px)]:hidden' },
  riga: { piena: 'flex shrink-0 items-stretch h-12', compatta: 'flex shrink-0 items-stretch h-10', auto: 'flex shrink-0 items-stretch h-12 [@media(max-height:720px)]:h-10' },
  bottone: { piena: 'h-12', compatta: 'h-10', auto: 'h-12 [@media(max-height:720px)]:h-10' },
} satisfies Record<string, Classi>;

/**
 * Tre parti FISSE: l'intestazione (numero e titolo) col testo del passo; il RIQUADRO DI STATO (attesa, «Non lì», altra, «Giusto!», e «tocca agli altri» mentre la coda di `dopo` si mostra), `role="status"`, sempre
 * nello stesso posto e della stessa altezza; la RIGA D'AZIONE (un testo muto in attesa, «Mostramelo» dopo il secondo errore, «Avanti» dopo la giusta o nei passi di sola lettura): 48 px in `piena`, 40 in `compatta`.
 * Dentro un passo la scheda non cambia mai altezza, quindi il tavolo non si sposta. Al cambio di passo una dissolvenza di sola opacita` (≤ 160 ms), che col movimento ridotto non c'e`. Il fuoco non si sposta da solo, salvo un caso: «Avanti» che si spegne MENTRE ha il fuoco lo passa al titolo del passo
 * (dentro il dialog) e a coda vuota lo riprende, se ce l'ha messo la scheda e ci e` ancora.
 * «Avanti» disabilitato (coda di `dopo` in corso) ha lo stile `disabled` della primitiva Button (fondo e testo neutri, non il teal): deve SEMBRARE spento, non solo esserlo per `aria-disabled`.
 */
export function SchedaGuida({ idTitolo, passi, indice, voci, attiva, riscontro, azione, avantiAttivo, mostra, lettura, formato, onAvanti, onMostramelo, testi }: PropsSchedaGuida) {
  const movimentoRidotto = useMovimentoRidotto();
  const contenuto = useRef<HTMLDivElement | null>(null);
  const riga = useRef<HTMLDivElement | null>(null);
  const titolo = useRef<HTMLHeadingElement | null>(null);
  // il fuoco che la scheda ha spostato lei dal bottone spento al titolo (v. l'effetto sotto): serve a riportarlo, e solo quello
  const fuocoSulTitolo = useRef(false);
  const avantiSpento = azione === 'avanti' && !avantiAttivo;
  const eraSpento = useRef(avantiSpento);
  // «Avanti» aveva il fuoco al momento del tocco? Si ricorda li`, perche` in Chromium il bottone che diventa `disabled` perde il fuoco SUBITO (document.activeElement e` gia` `body` quando l'effetto gira: misurato in Chromium 151, #878)
  const fuocoAlTocco = useRef(false);
  const compatta = formato === 'compatta';
  // in LETTURA il riquadro ✋ con il `come` VUOTO (il caso normale: una lettura non ha un gesto da dire) non ha niente da dire: nella scheda piena tiene lo spazio ma non si vede ne` si legge (fondo trasparente, voce
  // `invisible` e aria-hidden), cosi` la scheda non salta d'altezza fra un passo di lettura e uno a mossa. Se il gioco da` un `come` alla lettura («guarda e tocca Avanti») il riquadro lo mostra come sempre. La REGIONE
  // `role="status"` resta sempre nell'albero: quando la coda di `dopo` (`altrui`) la riempie l'annuncio non dipende da una regione appena mostrata. La compatta e` gia` `sr-only` e non si tocca.
  const muto = lettura && riscontro === 'attesa' && !compatta && (voci.find((v) => v.chiave === attiva)?.testo ?? '').trim() === '';
  const suAvanti = (e: MouseEvent<HTMLButtonElement>) => {
    fuocoAlTocco.current = document.activeElement === e.currentTarget;
    onAvanti();
  };
  // la coda di `dopo` di una lettura e` in corso: «Avanti» (spento) dice perche` nel bottone, in compatta
  const turnoDegliAltri = lettura && riscontro === 'altrui' && testi.turnoAltrui !== undefined;
  const etichettaAvanti = (
    <>
      {testi.avanti}
      <ArrowRight size={18} aria-hidden="true" />
    </>
  );
  const etichettaAltrui = (
    <>
      <Hourglass size={18} aria-hidden="true" className="shrink-0" />
      <span data-etichetta-altrui className="whitespace-nowrap">
        {testi.turnoAltrui}
      </span>
    </>
  );

  // al cambio di passo, e solo allora: il contenuto entra in dissolvenza
  useLayoutEffect(() => {
    const el = contenuto.current;
    if (movimentoRidotto || el === null || typeof el.animate !== 'function') return;
    const animazione = el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: DISSOLVENZA_MS, easing: 'ease-out' });
    return () => animazione.cancel();
  }, [indice, movimentoRidotto]);

  // «Avanti» si spegne (la coda di `dopo` e` in corso) mentre ha il fuoco: il `disabled` NATIVO lo toglie e il fuoco cade su `document.body`, FUORI dal dialog `aria-modal` (misurato: Chromium 151, base 304bd342). Allora il fuoco va sul titolo del passo
  // (`tabIndex=-1`, dentro il dialog); quando «Avanti» si riaccende, a coda vuota, torna li`, ma solo se sul titolo ce l'ha messo questa scheda e ci e` ancora (un fuoco che chi usa ha spostato non si tocca).
  // Il `disabled` resta quello nativo: lo stile `disabled:` della primitiva Button e` di tutta l'app.
  useLayoutEffect(() => {
    const prima = eraSpento.current;
    eraSpento.current = avantiSpento;
    const avutoIlFuoco = fuocoAlTocco.current;
    fuocoAlTocco.current = false;
    const bottone = riga.current?.querySelector<HTMLElement>('[data-azione="avanti"]') ?? null;
    const intestazione = titolo.current;
    if (avantiSpento && !prima) {
      const fuoco = document.activeElement;
      if (avutoIlFuoco && bottone !== null && intestazione !== null && (fuoco === bottone || fuoco === document.body || fuoco === null)) {
        intestazione.focus({ preventScroll: true });
        fuocoSulTitolo.current = true;
      }
    } else if (!avantiSpento) {
      if (fuocoSulTitolo.current && bottone !== null && document.activeElement === intestazione) bottone.focus({ preventScroll: true });
      fuocoSulTitolo.current = false;
    }
  });

  return (
    <div data-scheda-guida data-formato={formato} className={CLASSI.scheda[formato]}>
      <div ref={contenuto} data-contenuto-scheda className={CLASSI.contenuto[formato]}>
        <div className="grid">
          {passi.map((p, i) => {
            const qui = i === indice;
            if (compatta && !qui) return null;
            return (
              <div key={p.id} data-testo-passo={p.id} aria-hidden={qui ? undefined : true} className={cn('flex flex-col gap-1 [grid-area:1/1]', !qui && CLASSI.nascosto[formato])}>
                <div className="flex items-center gap-2">
                  <span aria-hidden="true" className={CLASSI.badge[formato]}>
                    {i + 1}
                  </span>
                  <h2 ref={qui ? titolo : undefined} id={qui ? idTitolo : undefined} tabIndex={qui ? -1 : undefined} className={cn(CLASSI.titolo[formato], 'outline-none')}>
                    {p.titolo}
                  </h2>
                </div>
                <p className={CLASSI.testo[formato]}>{p.testo}</p>
              </div>
            );
          })}
        </div>
        <div role="status" aria-live="polite" data-riquadro-stato data-riscontro={riscontro} className={cn(lettura ? RIQUADRO_IN_LETTURA[formato] : CLASSI.riquadro[formato], muto ? 'bg-transparent' : TONO[riscontro])}>
          {voci.map((v) => {
            const qui = v.chiave === attiva;
            if (compatta && !qui) return null;
            const IconaVoce = ICONA[v.riscontro];
            const nascostaAlTatto = !qui || muto;
            return (
              <p key={v.chiave} data-voce-stato={v.chiave} aria-hidden={nascostaAlTatto ? true : undefined} className={cn(CLASSI.voce[formato], !qui && CLASSI.nascosto[formato], qui && muto && 'invisible')}>
                <IconaVoce size={18} aria-hidden="true" className="mt-px shrink-0" />
                <span>{v.testo}</span>
              </p>
            );
          })}
        </div>
      </div>
      <div ref={riga} data-riga-azione className={CLASSI.riga[formato]}>
        {azione === 'avanti' ? (
          <Button variant="primary" fullWidth className={cn(AREA_DI_TOCCO, CLASSI.bottone[formato])} data-azione="avanti" disabled={!avantiAttivo} aria-disabled={!avantiAttivo} onClick={suAvanti}>
            {!turnoDegliAltri || formato === 'piena' ? (
              etichettaAvanti
            ) : formato === 'compatta' ? (
              etichettaAltrui
            ) : (
              <>
                <span className="inline-flex items-center gap-2 [@media(max-height:720px)]:hidden">{etichettaAvanti}</span>
                <span className="hidden items-center gap-2 [@media(max-height:720px)]:inline-flex">{etichettaAltrui}</span>
              </>
            )}
          </Button>
        ) : azione === 'mostramelo' ? (
          <Button variant="secondary" fullWidth className={cn(AREA_DI_TOCCO, CLASSI.bottone[formato])} data-azione="mostramelo" aria-pressed={mostra} onClick={onMostramelo}>
            {testi.mostramelo}
          </Button>
        ) : (
          <p data-azione-muta className="flex w-full items-center justify-center rounded-xl border border-dashed border-border px-3 text-center text-sm text-fg-muted">
            {testi.faiLaMossa}
          </p>
        )}
      </div>
    </div>
  );
}
